import React from "react";
import { LockKey, Sparkle, Wallet, ArrowsClockwise, Compass } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useToolUnlock } from "../lib/toolUnlock";
import { useCurrency } from "../context/CurrencyContext";
import usePlatform from "../hooks/usePlatform";
import { maybeOfferVipAfterUnlock } from "../lib/vipUpsell";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { toast } from "sonner";

/**
 * <PaywallGate>
 *   {locked-content}
 * </PaywallGate>
 *
 * Renders children with a blurred overlay + a paywall card *unless* the
 * signed-in customer has unlocked this (tool, chart) pair (or `unlocked` is
 * forced true via prop). On unlock success, the overlay disappears.
 *
 * Props:
 *   tool          — backend tool key (e.g. "bhrigu")
 *   toolLabel     — human label (for headline + razorpay description)
 *   birthDetails  — { date, time, place, timezone }
 *   children      — the premium content to gate
 *   className     — extra classes for the wrapper
 *   compact       — small variant (smaller paywall card)
 *
 * The component is safe to use even when the user is not signed in or has
 * not yet submitted birth details — it stays inactive in those cases.
 */
export default function PaywallGate({
  tool,
  toolLabel,
  birthDetails,
  report,
  onUnlocked,
  children,
  className = "",
  compact = false,
  variant = "blur", // "blur" (default) shows blurred teaser; "standalone" renders just the paywall card, no teaser
  pitch = "",       // optional override for the paywall body copy
  forWhat = "for this chart", // headline suffix — e.g. "for this search"
  footnote = "",    // optional override for the small print under the CTAs
}) {
  const { user, checkAuth } = useAuth();
  const { formatPrice, loading: currencyLoading, currency } = useCurrency();
  const { hidePurchases } = usePlatform();
  const isUsd = currency === "USD";
  const taxLabel = isUsd ? "Taxes (18%)" : "GST (18%)";
  const hasBirth = !!(birthDetails?.date && birthDetails?.time);
  const { unlocked, walletBalance, unlockPrice, paying, payWithWallet, payWithRazorpay, fingerprint, loading } =
    useToolUnlock({ tool, birthDetails, user, report, onSuccess: () => checkAuth?.() });
  // GST/Taxes added ON TOP for Razorpay payments. Wallet payments do NOT
  // get an extra 18% since the funds were already taxed at recharge time.
  const gstAmount = Math.round(unlockPrice * 0.18 * 100) / 100;
  const totalWithGst = Math.round((unlockPrice + gstAmount) * 100) / 100;

  // Bundle the two async-fetched dependencies into one flag. PaywallGate
  // shouldn't render its CTA prices/balance until BOTH the currency rate
  // AND the unlock-status pricing have resolved — otherwise US visitors
  // see the INR fallback (₹333 / ₹0) for a split-second before it
  // auto-rewrites to the correct localised values.
  const stillResolving = loading || currencyLoading;

  // Mirror the unlocked flag up to the parent so it can do bookkeeping
  // (e.g. Rudramsha attaches the Claude narrative to the saved report
  // once unlock flips true). Fires on both fresh payments AND on first
  // hydration if the user was already unlocked from a prior visit.
  const unlockedRef = React.useRef(false);
  React.useEffect(() => {
    if (unlocked && !unlockedRef.current) {
      unlockedRef.current = true;
      onUnlocked?.();
    }
  }, [unlocked, onUnlocked]);

  // If not signed in *or* no birth details submitted yet, gate everything behind a sign-in prompt.
  // Once signed in + birth details submitted, the gate is only blurred for genuinely-locked content.
  const showOverlay = !unlocked;

  if (!showOverlay) {
    return <div className={className}>{children}</div>;
  }

  const handleWalletPay = async () => {
    if (!user) return toast.error("Please sign in to unlock.");
    if (!hasBirth) return toast.error("Submit your birth details first.");
    const r = await payWithWallet();
    if (r?.ok) {
      toast.success("Unlocked! Enjoy your deep reading.");
      maybeOfferVipAfterUnlock(user);
    } else if (r?.error) toast.error(r.error);
  };

  const handleRazorpayPay = async () => {
    if (!user) return toast.error("Please sign in to unlock.");
    if (!hasBirth) return toast.error("Submit your birth details first.");
    const r = await payWithRazorpay();
    if (r?.ok) {
      toast.success("Payment confirmed! Premium reading unlocked.");
      maybeOfferVipAfterUnlock(user);
    } else if (r?.error && !/cancel/i.test(r.error)) toast.error(r.error);
  };

  const canWalletPay = !!user && walletBalance >= unlockPrice;

  // iOS Reader-mode: no in-app unlock button. Show a compliant
  // "unlock this on the web" card instead of the Razorpay/wallet CTAs.
  if (hidePurchases) {
    return (
      <div className={`relative ${className}`} data-testid={`paywall-gate-ios-${tool}`}>
        {variant === "blur" && (
          <>
            <div
              aria-hidden="true"
              className="select-none pointer-events-none filter blur-[6px] saturate-[0.85] opacity-90"
              style={{ maxHeight: compact ? "320px" : "640px", overflow: "hidden" }}
            >
              {children}
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent via-[#FAF4E8]/85 to-[#FAF4E8]" />
          </>
        )}
        <div className={variant === "blur" ? "absolute inset-x-0 bottom-0 px-3 sm:px-6 pb-4 sm:pb-6 flex justify-center" : "w-full flex justify-center py-6 sm:py-10"}>
          <div className="card-surface rounded-md p-6 max-w-md w-full text-center border border-gold/40 bg-[#FFF6E0]">
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Compass size={14} weight="fill" /> Unlock on the web
            </div>
            <h4 className="font-serif-display text-xl sm:text-2xl mt-2 font-light">
              Continue this {toolLabel || "reading"} on astrokuberchat.com
            </h4>
            <p className="text-muted text-sm mt-3 font-light">
              To keep the AstroKuberChat iOS app light and private, premium
              unlocks happen on the web. Once you unlock there, this page
              opens instantly the next time you refresh.
            </p>
            <button
              onClick={() => {
                try { window.open(`https://astrokuberchat.com/?utm_source=ios_app&unlock=${encodeURIComponent(tool)}`, "_blank"); } catch { /* ignore */ }
              }}
              className="btn-primary rounded-full px-6 py-3 mt-5 text-sm font-medium inline-flex items-center gap-2"
              data-testid={`paywall-ios-open-web-${tool}`}
            >
              <Sparkle size={14} weight="fill" />
              Open on astrokuberchat.com
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid={`paywall-gate-${tool}`}>
      {/* STANDALONE variant — no blurred teaser, just the paywall card centered.
          Used by tools that should show *nothing* until the user pays
          (Life Replay, Varshaphal, Rudramsha) to prevent any Universal-Key
          burn or free-preview signal before payment. */}
      {variant === "standalone" ? (
        <div className="w-full flex justify-center py-6 sm:py-10">
          <PaywallCard
            tool={tool} toolLabel={toolLabel} user={user} hasBirth={hasBirth}
            stillResolving={stillResolving} unlockPrice={unlockPrice}
            walletBalance={walletBalance} canWalletPay={canWalletPay}
            paying={paying} loading={loading} formatPrice={formatPrice}
            taxLabel={taxLabel} gstAmount={gstAmount} totalWithGst={totalWithGst}
            handleWalletPay={handleWalletPay} handleRazorpayPay={handleRazorpayPay}
            pitch={pitch} forWhat={forWhat} footnote={footnote}
          />
        </div>
      ) : (
        <>
          {/* Blurred premium content */}
          <div
            aria-hidden="true"
            className="select-none pointer-events-none filter blur-[6px] saturate-[0.85] opacity-90"
            style={{ maxHeight: compact ? "320px" : "640px", overflow: "hidden" }}
          >
            {children}
          </div>

          {/* Fading mask */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent via-[#FAF4E8]/85 to-[#FAF4E8]" />

          {/* Paywall card */}
          <div className="absolute inset-x-0 bottom-0 px-3 sm:px-6 pb-4 sm:pb-6 flex justify-center">
            <PaywallCard
              tool={tool} toolLabel={toolLabel} user={user} hasBirth={hasBirth}
              stillResolving={stillResolving} unlockPrice={unlockPrice}
              walletBalance={walletBalance} canWalletPay={canWalletPay}
              paying={paying} loading={loading} formatPrice={formatPrice}
              taxLabel={taxLabel} gstAmount={gstAmount} totalWithGst={totalWithGst}
              handleWalletPay={handleWalletPay} handleRazorpayPay={handleRazorpayPay}
              pitch={pitch} forWhat={forWhat} footnote={footnote}
            />
          </div>
        </>
      )}
    </div>
  );
}

function PaywallCard({
  tool, toolLabel, user, hasBirth, stillResolving, unlockPrice, walletBalance,
  canWalletPay, paying, loading, formatPrice, taxLabel, gstAmount, totalWithGst,
  handleWalletPay, handleRazorpayPay, pitch, forWhat, footnote,
}) {
  return (
        <div
          className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-amber-300/60 p-5 sm:p-6"
          data-testid={`paywall-card-${tool}`}
        >
          <div className="flex items-center gap-2 text-amber-700 label-eyebrow !text-[10px]">
            <LockKey size={14} weight="duotone" /> {forWhat === "for this chart" ? "Premium reading · this chart" : `Premium unlock · ${forWhat.replace(/^for /, "")}`}
          </div>
          <h3 className="font-serif-display text-xl sm:text-2xl text-[#3F2D1B] mt-2 leading-tight">
            Unlock the full <span className="italic text-gold">{toolLabel}</span> {forWhat || "for this chart"}
          </h3>
          <p className="text-xs sm:text-sm text-[#3F2D1B]/70 mt-2 leading-relaxed">
            {pitch ? pitch : stillResolving ? (
              <span className="inline-block h-3 w-48 rounded bg-amber-200/60 animate-pulse align-middle" />
            ) : (
              <>
                {formatPrice(unlockPrice)} unlocks every premium insight, transit hit and remedy for this birth-chart — permanently, across all your devices.
              </>
            )}
          </p>

          {!user ? (
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + window.location.pathname + window.location.search + window.location.hash;
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                startGoogleSignIn(redirectUrl);
              }}
              className="btn-primary rounded-full w-full mt-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2"
              data-testid={`paywall-${tool}-signin-btn`}
            >
              <Sparkle size={14} weight="fill" /> Sign in to unlock
            </button>
          ) : stillResolving ? (
            // Skeleton CTA stack while currency rate + unlock pricing
            // resolve. Replaces the previous flash-of-stale ₹333 + ₹0 stub
            // that appeared on first paint for non-INR visitors.
            <div className="mt-4 space-y-2" data-testid={`paywall-${tool}-loading`}>
              <div className="rounded-full w-full py-2.5 bg-amber-100/50 animate-pulse h-10" />
              <div className="rounded-full w-full py-2.5 bg-amber-50 border border-amber-200/60 animate-pulse h-10" />
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={handleWalletPay}
                disabled={!canWalletPay || paying || loading}
                className={`rounded-full w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition ${
                  canWalletPay
                    ? "btn-primary"
                    : "bg-amber-100 text-amber-900/60 cursor-not-allowed border border-amber-200"
                }`}
                data-testid={`paywall-${tool}-wallet-btn`}
              >
                <Wallet size={14} weight="duotone" />
                {paying
                  ? "Unlocking..."
                  : canWalletPay
                    ? `Pay ${formatPrice(unlockPrice)} from wallet (${formatPrice(walletBalance)} available)`
                    : `Wallet has ${formatPrice(walletBalance)} — not enough`}
              </button>
              <button
                type="button"
                onClick={handleRazorpayPay}
                disabled={paying || loading}
                className="rounded-full w-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 border border-amber-400/50 bg-amber-50 text-[#3F2D1B] hover:bg-amber-100 transition disabled:opacity-60"
                data-testid={`paywall-${tool}-rzp-btn`}
              >
                <ArrowsClockwise size={14} weight="duotone" />
                Pay {formatPrice(totalWithGst)} via Razorpay
              </button>
              <div className="text-[10px] text-[#3F2D1B]/60 text-center pt-0.5" data-testid={`paywall-${tool}-gst-breakdown`}>
                {formatPrice(unlockPrice)} + {taxLabel} {formatPrice(gstAmount)} = <span className="font-semibold">{formatPrice(totalWithGst)}</span>
              </div>
            </div>
          )}

          <div className="mt-3 text-[10px] text-[#3F2D1B]/55 leading-snug text-center">
            {footnote || `One-time payment · unlocks only ${forWhat.replace(/^for /, "")} for this tool · works forever`}
          </div>
        </div>
  );
}
